/*
 * ****************************************************************
 * File: Appbar.js
 * Project: CPaaS
 * Owner: Radisys
 *
 * Copyright - 2021 Radisys Corporation.
 * All rights reserved. No part of this software may be used or
 * reproduced in any form by any means without the prior written
 * permission of Radisys Corporation.
 *
 * Author: Pavan Kumar
 *
 * ****************************************************************
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  useMediaQuery,
  useTheme,
  Button,
  Switch,
  DialogActions,
} from "@material-ui/core";
import StatusIcon from "@material-ui/icons/FiberManualRecord";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MediaSelector from "../pages/home/mediaSelector";
import Slide from "@material-ui/core/Slide";
import { Close } from "@material-ui/icons";
import LogOutIcon from "../assets/SignOut_Icon.svg";
import MoreIcon from "../assets/More_icon.png";
import logo from "../assets/Logo.svg";
import Debug from "../assets/Debug.svg";
import SettingsIcon from "../assets/Settings.svg";
import userIcon from "../assets/Profile_Icon.svg";
import Alert_Icon_red from "../assets/Alert_Icon_red.svg";
import Conference_Icon from "../assets/conference-Icon.svg";
import { toast } from "react-toastify";
import PopupMenu from "../components/PopupMenu";
import MoreMenu from "../components/MoreMenu";
// Dialog Transition
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

// Styles
const useStyles = makeStyles({
  dialog: {
    position: "absolute",
    display: "flex",
    flexDirection: "column",
    bottom: 5,
    right: 5,
    color: "white",
    maxHeight: "60%",
    minWidth: "27%",
    margin: 0,
  },
  ibuttonclose: {
    color: "tomato",
    width: 40,
    height: 40,
    margin: "0.5rem",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
  },
  content: {
    flexGrow: 1,
  },
  dialogData: {
    display: "flex",
    justifyContent: "center",
    color: "white",
    width: "400px",
    margin: 0,
  },
});

const AppBarTop = ({
  RTCUtils,
  debugLogs,
  connection,
  onLogout,
  session,
  engageClient,
  readyEvent,
  test = false,
  resolutionData,
  makeCall,
  conferenceLayout,
  setConferenceLayout,
  onLayoutChange,
  onStartTranscribe,
  onStopTranscribe,
  toggleTranscribe,
}) => {
  const classes = useStyles();
  const [devicePopOverEl, setDevicePopOverEl] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [logoutAlert, setLogoutAlert] = useState(false);
  const [upgrade, setUpgrade] = useState(false);
  const [joinWithVideoMuted, setJoinWithVideoMuted] = useState(false);
  const [loggedValue, setLoggedValue] = React.useState(true);
  const [Sip, setSip] = React.useState();
  const [dialogData, setDialogData] = React.useState(false);
  // Device Settings
  const [devices, setDevices] = useState({
    camera: [],
    speaker: [],
    input: [],
    selected: {
      camera: null,
      speaker: null,
      input: null,
    },
  });
  const [popover, setPopover] = useState(null);
  const [moreMenu, setMoreMenu] = useState(null);

  const onLogoutHandler = () => {
    const { session: instance, ringing } = session;
    if (instance && !ringing) {
      setLogoutAlert(true);
      return;
    }
    onLogout();
  };

  const onDeviceSelected = (deviceType, deviceLabel) => {
    setDialogData(true);
    const device = devices[deviceType];
    const selectedDevice = device.filter((e) => e.label === deviceLabel)[0];
    switch (deviceType) {
      case "speaker": {
        RTCUtils.setAudioOutputDevice(selectedDevice.deviceId);
        break;
      }
      case "input":
        RTCUtils.setAudioInputDevice(selectedDevice.deviceId);
        break;
      case "camera":
        RTCUtils.setVideoInputDevice(selectedDevice.deviceId);
        break;
    }
    setDevices({
      ...devices,
      selected: {
        ...devices.selected,
        [deviceType]: selectedDevice.label,
      },
    });
  };

  const deviceChange = () => {
    debugLogs.current.push("System: Device Change Observed");
    toast.info("Device Change Observed");
    const input = RTCUtils.getAudioInputDevices();
    const speaker = RTCUtils.getAudioOutputDevices();
    const camera = RTCUtils.getVideoInputDevices();
    setDevices({
      ...devices,
      input,
      speaker,
      camera,
    });
  };

  const audioOutputDevice = (deviceId) => {
    const remoteStreamReference = document.getElementById("remoteStream");
    if (remoteStreamReference) {
      remoteStreamReference.setSinkId(deviceId).then(() => { });
    } else {
      // Suppose the DOM Element is unavailable due to state transitions. The
      // Audio change will wait for 2 Seconds
      setTimeout(() => {
        const remoteStreamReference = document.getElementById("remoteStream");
        if (remoteStreamReference) {
          console.log("speaker changed stream2", remoteStreamReference);
          remoteStreamReference.setSinkId(deviceId).then(() => { });
        }
      }, 2000);
    }
  };

  useEffect(() => {
    if (RTCUtils) {
      RTCUtils.addEventHandler("audiooutputdevice", audioOutputDevice);

      RTCUtils.addEventHandler("errorinfo", (error) => {
        console.log("RTC-errorInfo", error);
        toast.error(error.errorMessage);
      });

      RTCUtils.addEventHandler("failed", (error) => {
        debugLogs.current.push(`Failed_event, ${error.errorMessage}`);
        console.log("RTC_failed_event", error);
        toast.error(error.errorMessage);
      });

      RTCUtils.addEventHandler("devicechange", deviceChange);

      RTCUtils.addEventHandler("deviceready", () => {
        debugLogs.current.push("System: Devices Ready for Read");
        const input = RTCUtils.getAudioInputDevices();
        const speaker = RTCUtils.getAudioOutputDevices();
        const camera = RTCUtils.getVideoInputDevices();
        setDevices({
          ...devices,
          input,
          speaker,
          camera,
        });
      });
    }
    return () => {
      if (RTCUtils) {
        RTCUtils.removeEventHandler("devicechange", deviceChange);
        RTCUtils.removeEventHandler("audiooutputdevice", audioOutputDevice);
      }
    };
  }, []);

  // Connection status
  const connectionStatusColor = {
    Connecting: "yellow",
    Connected: "green",
    Disconnected: "grey",
  };

  useEffect(() => {
    sessionStorage.setItem("upgrade", upgrade);
  }, [upgrade]);

  useEffect(() => {
    sessionStorage.setItem("joinmuted", joinWithVideoMuted);
  }, [joinWithVideoMuted]);

  //  Handlers
  const handleDevicePopOverClick = (event) => {
    setDevicePopOverEl(event.currentTarget);
  };

  const handleDevicePopOverClose = () => {
    RTCUtils.streamClose();
    setDevicePopOverEl(null);
    /*  In iphone safari browser, click setting page & come out, audio-video was stoping due to device limitation,
        following code will add the localvodeo to make flow fine. */
    if (!dialogData) {
      if (/iPhone|iPod|iPad/i.test(navigator.userAgent)) {
        if (RTCUtils.getSelectedCameraResolution() !== undefined) {
          RTCUtils.setCameraResolution(RTCUtils.getSelectedCameraResolution());
        } else {
          RTCUtils.setCameraResolution("SD");
        }
      }
    }
  };

  // BreakPoints
  const theme = useTheme();
  const isMatch = useMediaQuery(theme.breakpoints.down("xs"));

  const handleLoggedValue = (event) => {
    setLoggedValue(event.target.checked);
  };

  useEffect(() => {
    if (engageClient && readyEvent) {
      engageClient.updateLogSettings({
        console: { enable: loggedValue },
        blob: { enable: loggedValue },
      });
    }
  }, [loggedValue]);

  useEffect(() => {
    if (engageClient !== null) {
      setSip(
        engageClient?.getUri()?.toString()
          ? engageClient?.getUri()?.toString()
          : ""
      );
    }
  }, [engageClient?.getUri()]);
  // Creation of text files
  const downloadTxtFile = () => {
    const list = engageClient?.getLogs();
    const element = document.createElement("a");
    const file = new Blob([JSON.stringify(list)], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `engagedigital_logs_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
  };

  let UserName = JSON.parse(localStorage.getItem("userName"));
  let Identity = JSON.parse(localStorage.getItem("identity"));

  useEffect(() => { }, [
    JSON.parse(localStorage.getItem("userName")),
    JSON.parse(localStorage.getItem("identity")),
    // localStorage.getItem("sip"),
  ]);

  const copyText = () => {
    navigator.clipboard.writeText(Sip);
    toast.info("Your SIP URI copied to clipboard");
  };

  const onClosePopover = () => {
    setMoreMenu(null)
  }

  return (
    <>
      {test && (
        <div>
          <div
            data-testid="onDeviceSelectedSpeaker"
            onClick={() => onDeviceSelected("speaker", "1")}
          ></div>
          <div
            data-testid="onDeviceSelectedInput"
            onClick={() => onDeviceSelected("input", "1")}
          ></div>
          <div
            data-testid="onDeviceSelectedCamera"
            onClick={() => onDeviceSelected("camera", "1")}
          ></div>
          <div data-testid="downloadTxtFile" onClick={downloadTxtFile}></div>
        </div>
      )}
      <AppBar color="inherit" style={{ height: isMatch ? "80px" : "10vh" }}>
        {isMatch === true ? (
          <Toolbar style={{ top: isMatch ? "5px" : "18px" }}>
            <Tooltip
              title={
                connectionStatusColor[connection.status] === "green"
                  ? "Connected"
                  : connectionStatusColor[connection.status] === "yellow"
                    ? "Connecting"
                    : "Disconnected"
              }
            >
              <Avatar
                style={{
                  background: connectionStatusColor[connection.status],
                  marginRight: "10px",
                }}
              >
                <img
                  src={userIcon}
                  style={{ width: "28px", height: "28px", color: "white" }}
                />
              </Avatar>
            </Tooltip>
            <p
              style={{
                color: "#333333",
                marginRight: "12px",
                fontSize: "14px",
              }}
            >
              {UserName?.trim() !== "" ? UserName : "Unknow User"}
              <p
                onClick={() => copyText()}
                style={{
                  fontSize: "12px",
                  marginTop: "-1px",
                  color: "#808080",
                  cursor: "pointer",
                }}
              >
                {Sip !== undefined
                  ? Sip.length <= 15
                    ? Sip
                    : `${Sip.substring(0, 15)}...`
                  : Identity}
              </p>
            </p>
            <div style={{ float: "right", marginLeft: "auto" }} />
            <Tooltip title={"Conference"}>
              <img
                width="34"
                height="34"
                src={Conference_Icon}
                style={{ marginRight: "12px" }}
                onClick={(e) => {
                  setPopover(e.currentTarget);
                }}
              />
            </Tooltip>
            <Tooltip title={"Debug Logs"}>
              <img
                src={Debug}
                style={{ marginRight: "12px" }}
                onClick={() => setDebugMode(!debugMode)}
              />
            </Tooltip>
            <Tooltip title={"Media Settings"}>
              <img
                style={{ width: "28px", height: "28px", marginRight: "10px" }}
                src={SettingsIcon}
                onClick={handleDevicePopOverClick}
              />
            </Tooltip>
            <Tooltip title={"logout"}>
              <img
                data-testid="logout"
                style={{ width: "28px", height: "28px", marginRight: "12px" }}
                src={LogOutIcon}
                onClick={() => onLogoutHandler()}
              />
            </Tooltip>
          </Toolbar>
        ) : (
          <Toolbar>
            <a href="https://www.radisys.com" rel="noreferrer" target="_blank">
              <img
                src={logo}
                style={{ width: "150px", height: "50px" }}
                alt="logo"
              />
            </a>
            <Typography
              data-testid="appbarText"
              variant="h6"
              style={{
                color: "#314B9F",
                textTransform: "uppercase",
                letterSpacing: "0px",
                opacity: 1,
                marginLeft: "12px",
                fontSize: "16px",
              }}
            >
              Programmable Video
            </Typography>

            <div style={{ float: "right", marginLeft: "auto" }} />

            <Tooltip
              title={
                connectionStatusColor[connection.status] === "green"
                  ? "Connected"
                  : connectionStatusColor[connection.status] === "yellow"
                    ? "Connecting"
                    : "Disconnected"
              }
            >
              <Avatar
                style={{
                  background: connectionStatusColor[connection.status],
                  marginRight: "10px",
                }}
              >
                <img
                  src={userIcon}
                  style={{ width: "28px", height: "28px", color: "white" }}
                />
              </Avatar>
            </Tooltip>
            <p
              data-testid="username"
              style={{
                color: "#333333",
                marginRight: "12px",
                fontSize: "18px",
              }}
            >
              {UserName?.trim() !== "" ? UserName : "Unknow User"}

              <p
                onClick={() => copyText()}
                data-testid="identity"
                style={{
                  fontSize: "12px",
                  marginTop: "-1px",
                  color: "#808080",
                  cursor: "pointer",
                }}
              >
                {Sip !== undefined ? Sip : Identity}
              </p>
            </p>
            <Tooltip title={"Conference"}>
              <img
                width="34"
                height="34"
                src={Conference_Icon}
                style={{ marginRight: "12px" }}
                onClick={(e) => {
                  setPopover(e.currentTarget);
                }}
              />
            </Tooltip>
            <Tooltip title={"Debug Logs"}>
              <img
                src={Debug}
                style={{ marginRight: "12px" }}
                onClick={() => setDebugMode(!debugMode)}
              />
            </Tooltip>
            <Tooltip title={"Media Settings"}>
              <img
                style={{ width: "28px", height: "28px", marginRight: "10px" }}
                src={SettingsIcon}
                onClick={handleDevicePopOverClick}
              />
            </Tooltip>
            <Tooltip title={"logout"}>
              <img
                data-testid="debugLogs"
                style={{ width: "28px", height: "28px", marginRight: "12px" }}
                src={LogOutIcon}
                onClick={() => onLogoutHandler()}
              />
            </Tooltip>
            <Tooltip title={"more"}>
              <img
                data-testid="moreIcon"
                style={{ width: "28px", height: "28px", marginRight: "12px" }}
                src={MoreIcon}
                onClick={(e) => {
                  setMoreMenu(e.currentTarget);
                }}
              />
            </Tooltip>
          </Toolbar>
        )}

        <Dialog
          classes={{
            paper: classes.dialog,
          }}
          TransitionComponent={Transition}
          open={debugMode}
          aria-labelledby="debug-logs"
          aria-describedby="debug logs contains all events generated by the SDK"
        >
          <div
            style={{
              color: "black",
              flexDirection: "row",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography
              data-testid="dialog-message"
              style={{ fontSize: 20, margin: "0.8rem" }}
            >
              Debug
            </Typography>
            <IconButton
              onClick={() => setDebugMode(false)}
              className={classes.ibuttonclose}
            >
              <Tooltip title={"Close Logs"}>
                <Close />
              </Tooltip>
            </IconButton>
          </div>
          <DialogContent dividers>
            {debugLogs.current.map((e, key) => {
              return (
                <div key={key} style={{ color: "black" }}>
                  <StatusIcon style={{ fontSize: "10px" }} /> {e}
                </div>
              );
            })}
          </DialogContent>
          <DialogActions
            style={{
              color: "black",
              flexDirection: "row",
              display: "flex",
              justifyContent: "center",
              alignSelf: "center",
            }}
          >
            <>
              <p
                style={{ color: "black", fontSize: "18px" }}
              >{`Enable SDK logs : ${loggedValue ? "on" : "off"}`}</p>
              <div style={{ marginTop: "1vh" }}>
                <Switch
                  color="primary"
                  checked={loggedValue}
                  onChange={handleLoggedValue}
                  name="loggedValue"
                />
              </div>
            </>
            <>
              {loggedValue ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: "0.4vh",
                  }}
                >
                  <Button
                    style={{ marginTop: "4px" }}
                    size="small"
                    variant="outlined"
                    onClick={() => downloadTxtFile()}
                  >
                    Download
                  </Button>
                </div>
              ) : null}
            </>
          </DialogActions>
        </Dialog>

        <Popover
          open={Boolean(devicePopOverEl)}
          anchorEl={devicePopOverEl}
          onClose={() => handleDevicePopOverClose()}
          anchorOrigin={{
            vertical: "right",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <MediaSelector
            RTCUtils={RTCUtils}
            session={session}
            hoverState={Boolean(devicePopOverEl)}
            devices={devices}
            onDeviceSelected={onDeviceSelected}
            closePopOver={() => handleDevicePopOverClose()}
            resolutionData={resolutionData}
            conferenceLayout={conferenceLayout}
            setConferenceLayout={setConferenceLayout}
            onLayoutChange={onLayoutChange}
          />
        </Popover>
        <Popover
          open={Boolean(popover)}
          anchorEl={popover}
          onClose={() => {
            setPopover(null);
          }}
          style={{ marginTop: "5.4em", marginLeft: "-4rem" }}
          // onClose={() => handleDevicePopOverClose()}
          anchorOrigin={{
            vertical: "right",
            horizontal: "bottom",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "bottom",
          }}
        >
          <PopupMenu setPopovers={setPopover} sip={Sip} makeCall={makeCall} />
        </Popover>
        <Popover
          open={Boolean(moreMenu)}
          anchorEl={moreMenu}
          onClose={() => {
            setMoreMenu(null);
          }}
          style={{ position: "fixed", right: '1%', top: '8.5%' }}
          // onClose={() => handleDevicePopOverClose()}
          anchorOrigin={{
            vertical: "right",
            horizontal: "bottom",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "bottom",
          }}
        >
          <MoreMenu
            setMoreMenu={setMoreMenu}
            session={session}
            onStartTranscribe={onStartTranscribe}
            onStopTranscribe={onStopTranscribe}
            toggleTranscribe={toggleTranscribe}
            onClosePopover={onClosePopover}
          />
        </Popover>
        <Dialog
          classes={{
            paper: classes.dialogData,
          }}
          style={{ margin: "0.4rem" }}
          TransitionComponent={Transition}
          open={logoutAlert}
          onClose={() => setLogoutAlert(false)}
          aria-labelledby="debug-logs"
          aria-describedby="debug logs contains all events generated by the SDK"
        >
          <DialogContent dividers>
            <Typography
              style={{
                color: "#314B9F",
                textTransform: "capitalize",
                letterSpacing: "0px",
                opacity: 1,
                fontWeight: "bold",
              }}
            >
              warning - window close
            </Typography>
            <div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "2vh",
                }}
              >
                <img
                  src={Alert_Icon_red}
                  alt=""
                  style={{
                    width: "48px",
                    height: "48px",
                    marginTop: "2vh",

                    margin: "1rem",
                  }}
                />
                <p style={{ color: "#737373" }}>
                  Ongoing call gets disconnected, would you like to end the call
                  and close window?
                </p>
              </div>
              <div
                style={{
                  marginTop: "3vh",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Button
                  data-testid="btn"
                  style={{
                    width: "100px",
                    height: "34px",
                    margin: "1rem",
                    color: "#314B9F",
                  }}
                  variant="outlined"
                  onClick={() => onLogout()}
                >
                  Ok
                </Button>
                <Button
                  data-testid="btn1"
                  onClick={() => setLogoutAlert(false)}
                  style={{
                    width: "100px",
                    height: "34px",
                    background: "#314B9F",
                    color: "#FFFF",
                    margin: "1rem",
                  }}
                  variant="contained"
                >
                  Exit
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </AppBar>
    </>
  );
};
AppBarTop.propTypes = {
  RTCUtils: PropTypes.any,
  devices: PropTypes.any,
  onDeviceSelected: PropTypes.any,
  debugLogs: PropTypes.any,
  connection: PropTypes.object.isRequired,
  session: PropTypes.any,
  makeCall: PropTypes.any,
  acceptCall: PropTypes.any,
  logoutClient: PropTypes.any,
  onLogout: PropTypes.any,
  endCall: PropTypes.any,
  connect: PropTypes.any,
  remoteURI: PropTypes.string.isRequired,
  setRemoteURI: PropTypes.func.isRequired,
  setUpgrade: PropTypes.any,
  upgrade: PropTypes.any,
  test: PropTypes.any,
  engageClient: PropTypes.any,
  readyEvent: PropTypes.bool,
  resolutionData: PropTypes.any,
  conferenceLayout: PropTypes.any,
  setConferenceLayout: PropTypes.any,
  onLayoutChange: PropTypes.any,
  onStartTranscribe: PropTypes.any,
  onStopTranscribe: PropTypes.any,
  toggleTranscribe: PropTypes.any,
};

export default AppBarTop;
